import { Storage } from '@lightningjs/sdk'

import SettingsMenuItem from '../components/SettingsMenu/SettingsMenuItem'
import {
  ACCOUNT_DETAILS,
  BASE_ROUTES,
  CUSTOMER_SUPPORT,
  DEVELOPER_SETTINGS,
  LIVE_SCORES,
  PRIVACY_POLICY,
  SUBSCRIPTION,
  TERMS_OF_SERVICE,
} from '../constants/routes'
import { DevSettingId, DevSettingType } from '../types/flo-enums'
import { getUserData, isUserSignedIn } from './authentication'
import { getText } from './dictionary'

// This acts as an inner router for the settings page
const sections = [
  { name: getText('accountDetails'), route: ACCOUNT_DETAILS },
  { name: getText('subscription'), route: SUBSCRIPTION },
  { name: getText('favorites'), route: BASE_ROUTES.EDIT_FAVORITES, state: { fromSettings: true } },
  { name: getText('liveScores'), route: LIVE_SCORES },
  { name: getText('customerSupport'), route: CUSTOMER_SUPPORT },
  { name: getText('termsOfService'), route: TERMS_OF_SERVICE },
  { name: getText('privacyPolicy'), route: PRIVACY_POLICY },
  { name: getText('developerSettings'), route: DEVELOPER_SETTINGS },
]

export const toMenuItemRef = (i) => `MenuItem-${i}`

export const createMenuItems = () => {
  const userData = getUserData()
  const isUserAuthenticated = isUserSignedIn()

  const shouldIncludeItem = (item) => {
    const name = item?.name
    const isDevSettings = name === getText('developerSettings')

    // Hide "favorites" if not authenticated
    if (!isUserAuthenticated && name === getText('favorites')) {
      return false
    }

    // Show "developerSettings" only if user is from FloSports OR staging is enabled
    if (isDevSettings && !(userData?.user?.email.includes('@flosports.tv') || isStagingEnabled())) {
      return false
    }

    return true
  }

  return sections.filter(shouldIncludeItem).map((item, i) => ({
    ref: toMenuItemRef(i),
    menuItemId: i,
    type: SettingsMenuItem,
    label: item?.name,
    route: item?.route,
    ...(item?.state && { routeState: item.state }),
  }))
}

// Developer settings
export const DEV_SETTINGS = 'devSettings'

export type DevSettingItem = {
  id: DevSettingId
  type: DevSettingType
  icon: string
  name: string
  isOn: boolean
}

export const devSettings = [
  {
    id: 'enable-staging',
    type: 'enable',
    icon: 'icon-bug',
    name: 'Staging',
    isOn: false,
  },
  {
    id: 'toggle-show-video-statistics',
    type: 'toggle',
    icon: '',
    name: 'Show video statistics',
    isOn: false,
  },
] as DevSettingItem[]

export const setDevSettings = () => {
  if (!Storage.get(DEV_SETTINGS)) {
    Storage.set(DEV_SETTINGS, devSettings)
  }
}

export const getDevSettings = () => {
  return Storage.get(DEV_SETTINGS)
}

export const toggleDevSetting = (id: DevSettingId, value: boolean) => {
  const devSettings = getDevSettings()
  const settingIndex = devSettings?.findIndex((item) => item.id === id)
  devSettings[settingIndex].isOn = value
  Storage.set(DEV_SETTINGS, devSettings)
}

export const isDevSettingEnabled = (id: DevSettingId) => {
  const devSettings = getDevSettings()

  if (devSettings) {
    const setting = devSettings?.find((item) => item.id === id)
    return setting?.isOn
  }

  return false
}

export const isStagingEnabled = () => {
  const devSettings = getDevSettings()

  if (devSettings) {
    return devSettings?.find((item) => item.id === 'enable-staging')?.isOn
  }

  return false
}
